<template>
  <main class="settings-page">
    <header>
      <div>
        <p>PLATFORM SETTINGS</p>
        <h1>系统配置</h1>
        <span>保存后立即生效；每次变更和回滚都会写入审计历史。</span>
      </div>
      <a-button @click="load"><i class="ri-refresh-line" />刷新</a-button>
    </header>
    <ServiceStatusEditor :can-manage="session.can('settings.manage')" />
    <a-alert
      type="info"
      show-icon
      message="密钥与部署配置不在此处管理"
      description="支付私钥、数据库、JWT、对象存储等由部署环境或 Secret Manager 注入；本页只维护运营规则与受控开关。"
    />
    <a-spin :spinning="loading"
      ><section v-for="group in groups" :key="group.name" class="settings-group">
        <h2>{{ group.name }}</h2>
        <a-card v-for="item in group.items" :key="item.key" size="small" class="setting-card"
          ><template #title>{{ item.label }}</template
          ><template #extra
            ><a-tag v-if="item.version" color="blue">v{{ item.version }}</a-tag
            ><a-tag v-else>默认值</a-tag></template
          >
          <p>{{ item.description }}</p>
          <div class="setting-value">
            <span>{{ display(item) }}</span
            ><a-space
              ><a-button size="small" @click="openHistory(item)">历史</a-button
              ><a-button
                v-if="item.editor !== 'readonly'"
                size="small"
                type="primary"
                @click="openEdit(item)"
                >编辑</a-button
              ></a-space
            >
          </div></a-card
        >
      </section></a-spin
    >
  </main>
  <a-modal
    v-model:open="editorOpen"
    :title="editing ? `编辑：${editing.label}` : '编辑配置'"
    :confirm-loading="saving"
    ok-text="立即生效"
    @ok="save"
    ><a-form layout="vertical"
      ><template v-if="editing?.editor === 'form'"
        ><a-form-item
          v-for="field in editing.fields"
          :key="field.path"
          :label="field.label"
          :extra="field.description"
          ><a-switch
            v-if="field.editor === 'switch'"
            :checked="Boolean(fieldValue(field.path))"
            @update:checked="setFieldValue(field.path, $event)" /><a-input-number
            v-else-if="field.editor === 'number'"
            :value="displayFieldValue(field)"
            :min="field.min"
            :max="field.max"
            :precision="field.precision ?? 0"
            style="width: 100%"
            @update:value="setFieldValue(field.path, $event, field.unit)" /><a-select
            v-else-if="field.editor === 'select'"
            :value="String(fieldValue(field.path) ?? '')"
            :options="field.options"
            style="width: 100%"
            @update:value="setFieldValue(field.path, $event)" /><a-input
            v-else
            :value="String(fieldValue(field.path) ?? '')"
            @update:value="setFieldValue(field.path, $event)" /></a-form-item></template
      ><a-form-item v-else label="配置值"
        ><a-switch
          v-if="editing?.editor === 'switch'"
          v-model:checked="switchValue" /><a-input-number
          v-else
          v-model:value="numberValue"
          :min="editing?.min"
          :max="editing?.max"
          :precision="0"
          style="width: 100%" /></a-form-item
      ><a-form-item label="变更原因" required
        ><a-input
          v-model:value="reason"
          :maxlength="200"
          show-count
          placeholder="说明本次调整的业务原因" /></a-form-item></a-form
  ></a-modal>
  <a-modal
    v-model:open="historyOpen"
    :title="historyTarget ? `${historyTarget.label}：历史版本` : '历史版本'"
    :footer="null"
    ><a-spin :spinning="historyLoading"
      ><a-empty v-if="!history.length" description="暂无历史记录" /><a-list
        v-else
        :data-source="history"
        ><template #renderItem="{ item }"
          ><a-list-item
            ><a-list-item-meta
              ><template #title>v{{ item.version }} · {{ item.reason }}</template
              ><template #description
                >{{ new Date(item.createdAt).toLocaleString('zh-CN', { hour12: false }) }} ·
                {{ displayValue(item.value) }}</template
              ></a-list-item-meta
            ><a-button
              :disabled="!historyTarget || item.version === historyTarget.version"
              size="small"
              @click="confirmRollback(item.version)"
              >回滚到此版本</a-button
            ></a-list-item
          ></template
        ></a-list
      ></a-spin
    ></a-modal
  >
<a-modal v-model:open="rollbackOpen" title="确认回滚系统配置" ok-text="确认回滚" cancel-text="取消" :confirm-loading="rollingBack" @ok="submitRollback">
    <p>回滚到 v{{ rollbackVersion }} 将立即替换当前生效配置。</p>
    <a-textarea v-model:value="rollbackReason" aria-label="回滚原因" placeholder="请输入业务原因" :maxlength="500" />
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, toRaw } from 'vue';
import { message } from 'ant-design-vue';
import type { AdminSettingCatalogItem, AdminSettingHistoryItem } from 'shared';
import { adminApi } from '@/api';
import { useAdminSessionStore } from '@/stores/admin-session.store';
import ServiceStatusEditor from '@/components/ServiceStatusEditor.vue';
const session = useAdminSessionStore();
const items = ref<AdminSettingCatalogItem[]>([]);
const loading = ref(false);
const editorOpen = ref(false);
const historyOpen = ref(false);
const saving = ref(false);
const historyLoading = ref(false);
const editing = ref<AdminSettingCatalogItem | null>(null);
const historyTarget = ref<AdminSettingCatalogItem | null>(null);
const history = ref<AdminSettingHistoryItem[]>([]);
const reason = ref('');
const switchValue = ref(false);
const numberValue = ref(0);
const formValue = ref<Record<string, any>>({});
const groups = computed(() => {
  const map = new Map<string, AdminSettingCatalogItem[]>();
  for (const item of items.value) map.set(item.group, [...(map.get(item.group) ?? []), item]);
  return [...map].map(([name, grouped]) => ({ name, items: grouped }));
});
async function load() {
  loading.value = true;
  try {
    items.value = await adminApi.get<AdminSettingCatalogItem[]>('/admin/settings/catalog');
  } catch {
    message.error('系统配置加载失败');
  } finally {
    loading.value = false;
  }
}
function displayValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? '已启用' : '已关闭';
  if (typeof value === 'number') return String(value);
  return value && typeof value === 'object' ? '已配置' : '—';
}
function display(item: AdminSettingCatalogItem): string {
  return displayValue(item.value);
}
function openEdit(item: AdminSettingCatalogItem) {
  editing.value = item;
  reason.value = '';
  switchValue.value = item.value === true;
  numberValue.value = typeof item.value === 'number' ? item.value : 0;
  formValue.value = structuredClone(
    item.value && typeof item.value === 'object' ? toRaw(item.value) : {},
  );
  editorOpen.value = true;
}
function fieldValue(path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (value, key) =>
        value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined,
      formValue.value,
    );
}
function displayFieldValue(field: NonNullable<AdminSettingCatalogItem['fields']>[number]): number {
  const value = Number(fieldValue(field.path) ?? 0);
  return field.unit === 'GiB' ? value / 1024 ** 3 : value;
}
function setFieldValue(path: string, value: unknown, unit?: 'GiB'): void {
  const keys = path.split('.');
  let target: Record<string, any> = formValue.value;
  for (const key of keys.slice(0, -1)) {
    if (!target[key] || typeof target[key] !== 'object') target[key] = {};
    target = target[key];
  }
  target[keys.at(-1)!] =
    unit === 'GiB' ? Math.round(Math.max(0, Number(value ?? 0)) * 1024 ** 3) : value;
}
function value(): unknown {
  if (editing.value?.editor === 'switch') return switchValue.value;
  if (editing.value?.editor === 'number') return numberValue.value;
  return formValue.value;
}
async function save() {
  if (!editing.value || !reason.value.trim()) {
    message.warning('请填写变更原因');
    return;
  }
  let next: unknown;
  try {
    next = value();
  } catch (error) {
    message.error(error instanceof Error ? error.message : '配置值无效');
    return;
  }
  saving.value = true;
  try {
    const saved = await adminApi.patch<{ version: number }>(
      `/admin/settings/${encodeURIComponent(editing.value.key)}`,
      { value: next, version: editing.value.version, reason: reason.value.trim() },
    );
    editing.value.value = next;
    editing.value.version = saved.version;
    message.success('配置已立即生效');
    editorOpen.value = false;
    await load();
  } catch {
    message.error('保存失败，可能已被其他管理员修改');
  } finally {
    saving.value = false;
  }
}
async function openHistory(item: AdminSettingCatalogItem) {
  historyTarget.value = item;
  historyOpen.value = true;
  history.value = [];
  historyLoading.value = true;
  try {
    history.value = await adminApi.get<AdminSettingHistoryItem[]>(
      `/admin/settings/${encodeURIComponent(item.key)}/history`,
    );
  } catch {
    message.error('配置历史加载失败');
  } finally {
    historyLoading.value = false;
  }
}
const rollbackOpen = ref(false);
const rollingBack = ref(false);
const rollbackVersion = ref(0);
const rollbackReason = ref('');
function confirmRollback(targetVersion: number): void {
  rollbackVersion.value = targetVersion;
  rollbackReason.value = '';
  rollbackOpen.value = true;
}
async function submitRollback(): Promise<void> {
  if (rollingBack.value) return;
  if (!rollbackReason.value.trim()) { message.warning('请填写回滚原因'); return; }
  rollingBack.value = true;
  try {
    await rollback(rollbackVersion.value, rollbackReason.value.trim());
    rollbackOpen.value = false;
  } catch { /* 错误已显示，保留填写的原因。 */ } finally { rollingBack.value = false; }
}
async function rollback(targetVersion: number, reason: string) {
  if (!historyTarget.value) return;
  const rollbackReason = reason;
  try {
    await adminApi.post(`/admin/settings/${encodeURIComponent(historyTarget.value.key)}/rollback`, {
      targetVersion,
      currentVersion: historyTarget.value.version,
      reason: rollbackReason,
    });
    message.success('已回滚并立即生效');
    await load();
    await openHistory(historyTarget.value);
  } catch {
    message.error('回滚失败，当前版本可能已变化');
    throw new Error('回滚失败，请核对当前版本后重试');
  }
}
void load();
</script>

<style scoped>
.settings-page {
  max-width: 1120px;
  margin: 0 auto;
  padding: 32px;
}
.settings-page > header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 20px;
}
.settings-page header p {
  margin: 0;
  color: #6258d9;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
}
.settings-page h1 {
  margin: 5px 0;
  color: #303143;
}
.settings-page header span,
.setting-card p {
  color: #858798;
}
.settings-group {
  margin-top: 24px;
}
.settings-group h2 {
  margin: 0 0 10px;
  color: #494a5c;
  font-size: 16px;
}
.setting-card {
  margin-bottom: 10px;
}
.setting-card p {
  margin: 0 0 12px;
  font-size: 13px;
}
.setting-value {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  font-family: ui-monospace, monospace;
  font-size: 12px;
  overflow: hidden;
}
.setting-value > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@media (max-width: 760px) {
  .settings-page {
    padding: 20px 14px;
  }
  .settings-page > header {
    display: block;
  }
  .settings-page > header .ant-btn {
    margin-top: 12px;
  }
  .setting-value {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
